import {
    DEFAULT_CONFIDENCE_THRESHOLD,
    adjustedConfidenceFromOwlvit,
    predictionToAnnotation,
    calculateBoxPromptConfidenceThreshold
} from "@rapid/utils/predictions";
import { useMedia } from "@rapid/contexts/MediaContext";
import { useModel } from "@rapid/contexts/ModelContext";
import {
    Prediction,
    StreamInferEventHandlers,
    StreamInferRequestBody
} from "@rapid/utils/api/inference";
import { usePromptLabels, useCurrentMedia } from "@rapid/store";
import { useCurrentWorkflow } from "@rapid/hooks/useCurrentWorkflow";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { useFlow } from "@rapid/contexts/FlowContext";
import { useFps } from "@rapid/contexts/FpsContext";
import { useComputedValues } from "@rapid/contexts/ComputedValuesContext";
import { createBatchedStateSetter } from "@rapid/utils/batchedQueue";
import useApi, { useAsyncBoundOperation } from "./useApi";
import { useCallback } from "react";
import {
    useInferenceQueue,
    useGetInferenceQueueItemById,
    useAddToInferenceQueue,
    useUpdateInferenceQueueItemStatus,
    useUpdateInferenceQueueItemProgress,
    useClearInferenceQueue,
    InferenceQueueItem,
    InferenceStatus
} from "@rapid/store";
import {
    InferenceTypes,
    DatasetUploadMode
} from "@functions/rapid/services/inference/stream/types";
import { useVideoSegmentation } from "@rapid/utils/videoSegmentation";
import { MediaItemMetadata, MediaItem } from "@rapid/types/media";
import { useMediaUtils } from "./useMediaUtils";
import { Annotation } from "@rapid/types/annotation";
import { useFlowState } from "./useFlowState";
import { ToolType, useSaveSharedEditHistory } from "./useSaveSharedEditHistory";
import { v4 as uuidv4 } from "uuid";
import { crossClassNMSWithExisting } from "@rapid/utils/nms";
import { smartFilterPredictions } from "@rapid/utils/predictionFiltering";

enum WorkflowOutputsKeys {
    MESSAGE = "message",
    PREDICTIONS = "predictions",
    VISUALIZATION = "visualization"
}

export type GetPredictionsForFileParams = {
    mediaItem: MediaItem;
    abortController?: AbortController;
    reviewModel?: boolean;
    onProgress?: (progress: number) => void;
    inferencePayload?: Partial<StreamInferRequestBody>;
    extractedFrameMeta?: { videoId: string; segment: number };
    inferenceType?: InferenceTypes;
    textPrompts?: string[];
    customPromptingData?: any[];
    isDraft?: boolean;
    batchFlushIntervalMs?: number;
    useInferenceQueue?: boolean;
    /**
     * When provided, run inference for this specific label only.
     * Results will be merged with existing predictions/annotations,
     * replacing only predictions for this specific label.
     */
    targetLabel?: string;
    /**
     * When provided, filter out predictions below this confidence threshold
     * before converting to annotations. Useful for box prompting where more
     * prompts can lead to more false positives.
     */
    confidenceThreshold?: number;
    /**
     * When true, annotations will be merged with existing ones instead of replacing them.
     * Used for mixed inference where SAM3 and OWLv2 run in parallel.
     * New annotations are filtered by their labels to only add/update for the specified textPrompts.
     */
    appendMode?: boolean;
    /**
     * When true, if the confidence-filtered predictions result in 0 new annotations
     * (after NMS deduplication), the highest-confidence prediction below the threshold
     * will be included anyway. This helps users who keep clicking "Find More Objects"
     * to eventually see lower-confidence predictions one at a time.
     */
    fallbackBelowThreshold?: boolean;

    /**
     * Callback to receive all parsed predictions (sorted by confidence) before filtering.
     * Used for caching predictions in "Find Similar Objects" to enable progressive reveal.
     * When provided, the callback is called with all predictions sorted by confidence descending.
     */
    onRawPredictions?: (predictions: Prediction[]) => void;
};

export type GetPredictionsForFileResult = { success: boolean };

export const useGetPredictions = () => {
    const { setFlowLocked } = useFlow();
    const { flow, step } = useFlowState();
    const { completedMediaItems } = useMedia();
    const {
        setPredictionsForFileAndFrameSegment,
        setAnnotationsForFileAndFrameId,
        clearPredictionsForFile,
        annotationsForFileAndFrameId,
        predictionsForFileAndFrameSegment
    } = useModel();
    const { currentPromptingData } = useComputedValues();
    const api = useApi();
    const promptLabels = usePromptLabels();
    const currentMedia = useCurrentMedia();
    const { updateMediaItem } = useMediaUtils();
    // Inference queue state management via store hooks
    const getInferenceQueue = useInferenceQueue();
    const getInferenceQueueItemById = useGetInferenceQueueItemById;
    const addToInferenceQueue = useAddToInferenceQueue();
    const updateInferenceQueueItemStatus = useUpdateInferenceQueueItemStatus();
    const updateInferenceQueueItemProgress = useUpdateInferenceQueueItemProgress();
    const clearInferenceQueue = useClearInferenceQueue();
    const { fps } = useFps();
    const { segmentForTargetTime } = useVideoSegmentation(
        currentMedia?.file.metadata?.videoMetadata?.duration || 0,
        fps
    );
    const { currentWorkflow: workflow } = useCurrentWorkflow();
    const { apiKey } = useCurrentWorkspace();
    const saveSharedEditHistory = useSaveSharedEditHistory();

    // simple helper to wait for the entire inference queue to complete
    const checkInferenceQueue = useCallback(async () => {
        const all = Promise.all(
            Array.from(getInferenceQueue.entries()).map(([key, item]) => item.promise)
        );

        const results = await all;
        return results;
    }, [getInferenceQueue]);
    const { isLoading: isLoadingInferenceQueue, error: errorInferenceQueue } =
        useAsyncBoundOperation(checkInferenceQueue, { autoInvoke: true });

    const clearQueue = useCallback(() => {
        // The clearInferenceQueue method now handles aborting controllers internally
        clearInferenceQueue();
    }, [clearInferenceQueue]);

    // Helper function to create isolated progress tracking for each getPredictionsForFile call
    const createProgressTracker = useCallback(
        ({ mockProgress }: { mockProgress: boolean } = { mockProgress: false }) => {
            let mockedProgress = 0;
            let mockedProgressInterval: NodeJS.Timeout | null = null;
            let progress = 0;

            const startProgressInterval = (onProgress: (progress: number) => void) => {
                mockedProgress = 0;

                if (!mockProgress) {
                    return;
                }

                // Slow initial progress: 0-25% over ~25 seconds (1000ms interval)
                mockedProgressInterval = setInterval(() => {
                    mockedProgress += 1;
                    onProgress?.(mockedProgress);
                    progress = mockedProgress;

                    // Cap at 25% and clear interval
                    if (mockedProgress >= 25) {
                        if (mockedProgressInterval) {
                            clearInterval(mockedProgressInterval);
                            mockedProgressInterval = null;
                        }
                    }
                }, 1000);
            };

            const updateProgress = (
                updatedProgress: number,
                onProgress: (progress: number) => void
            ) => {
                const progressBuffer = 5;

                if (updatedProgress > mockedProgress + progressBuffer) {
                    if (mockedProgressInterval) {
                        clearInterval(mockedProgressInterval);
                        mockedProgressInterval = null;
                    }

                    onProgress?.(updatedProgress);
                    progress = updatedProgress;
                    mockedProgress = updatedProgress; // Sync mocked progress to prevent restart
                }
            };

            const cleanup = () => {
                if (mockedProgressInterval) {
                    clearInterval(mockedProgressInterval);
                    mockedProgressInterval = null;
                }
            };

            return { startProgressInterval, updateProgress, cleanup, progress };
        },
        []
    );

    const parseOwlvitPredictions = (predictions: any) => {
        return predictions.map((p: any) => ({
            x: p.x,
            y: p.y,
            width: p.width,
            height: p.height,
            confidence: adjustedConfidenceFromOwlvit(p.confidence),
            class: p.class,
            class_id: p.class_id,
            // Always generate unique IDs to prevent duplicate detection_ids from OWLv2
            // causing multiple annotations to be deleted together
            detection_id: uuidv4(),
            label: p.class // this is redundant. TODO: implement consistent prediction schema
        }));
    };

    const parseWorkflowPredictions = (workflowResults: any) => {
        const { outputs = [] } = workflowResults;

        const predictionsOutput = outputs.find((o: any) => o[WorkflowOutputsKeys.PREDICTIONS]);

        if (!predictionsOutput) {
            return [];
        }

        const predictions = predictionsOutput[WorkflowOutputsKeys.PREDICTIONS]?.predictions || [];

        return predictions.map((p: any) => ({
            x: p.x,
            y: p.y,
            width: p.width,
            height: p.height,
            confidence: p.confidence,
            class: p.class,
            class_id: p.class_id,
            detection_id: p.detection_id,
            label: `${p.class} (${Math.round(p.confidence * 100)}%)`
        }));
    };

    const parseSam3Predictions = (predictions: any) => {
        return predictions.map((prediction, index) => {
            let x: number, y: number, width: number, height: number;
            let masks: any = null;

            // Handle mask predictions (RLE format from SAM3)
            if (prediction.type === "mask" && prediction.masks) {
                masks = prediction.masks;

                const maskWidth = masks.size[1]; // COCO format: size = [height, width]
                const maskHeight = masks.size[0];

                const bounds = calculateRLEMaskBounds(masks.counts, maskWidth, maskHeight);

                if (bounds) {
                    x = (bounds.minX + bounds.maxX) / 2;
                    y = (bounds.minY + bounds.maxY) / 2;
                    width = bounds.maxX - bounds.minX;
                    height = bounds.maxY - bounds.minY;
                } else {
                    x = maskWidth / 2;
                    y = maskHeight / 2;
                    width = maskWidth;
                    height = maskHeight;
                }
            }
            // Handle polygon predictions (legacy/fallback)
            else if (prediction.vertices) {
                let minX = Infinity,
                    minY = Infinity,
                    maxX = -Infinity,
                    maxY = -Infinity;

                prediction.vertices.forEach((vertex: any) => {
                    vertex.forEach(({ x, y }: { x: number; y: number }) => {
                        minX = Math.min(minX, x);
                        minY = Math.min(minY, y);
                        maxX = Math.max(maxX, x);
                        maxY = Math.max(maxY, y);
                    });
                });

                x = (minX + maxX) / 2;
                y = (minY + maxY) / 2;
                width = maxX - minX;
                height = maxY - minY;
            } else {
                x = y = width = height = 0;
            }

            return {
                x,
                y,
                width,
                height,
                confidence: prediction.confidence,
                class: prediction.class,
                class_id: 0,
                detection_id: prediction.detection_id || uuidv4(),
                label: prediction.class,
                masks,
                type: prediction.type || "polygon"
            };
        });
    };

    // Calculate bounding box from RLE mask
    const calculateRLEMaskBounds = (
        rleString: string,
        width: number,
        height: number
    ): { minX: number; minY: number; maxX: number; maxY: number } | null => {
        let minX = width;
        let minY = height;
        let maxX = 0;
        let maxY = 0;
        let found = false;

        try {
            const counts: number[] = [];
            let p = 0;
            let m = 0;

            while (p < rleString.length) {
                let x = 0;
                let k = 0;
                let more = 1;

                while (more) {
                    const c = rleString.charCodeAt(p) - 48;
                    x |= (c & 0x1f) << (5 * k);
                    more = c & 0x20;
                    p++;
                    k++;
                    if (!more && c & 0x10) {
                        x |= -1 << (5 * k);
                    }
                }

                if (m > 2) {
                    x += counts[m - 2];
                }
                counts[m++] = x;
            }

            let idx = 0;
            let val = 0;

            for (const count of counts) {
                if (val === 1) {
                    for (let i = 0; i < count; i++) {
                        const col = Math.floor(idx / height);
                        const row = idx % height;
                        if (col < width && row < height) {
                            minX = Math.min(minX, col);
                            minY = Math.min(minY, row);
                            maxX = Math.max(maxX, col);
                            maxY = Math.max(maxY, row);
                            found = true;
                        }
                        idx++;
                    }
                } else {
                    idx += count;
                }
                val = 1 - val;
            }

            if (!found) {
                return null;
            }

            return { minX, minY, maxX, maxY };
        } catch (error) {
            console.error("Error calculating RLE mask bounds:", error);
            return null;
        }
    };

    // Generate predictions for a single file using streaming inference
    const getPredictionsForFile = useCallback(
        async ({
            mediaItem,
            abortController,
            reviewModel = false,
            onProgress,
            inferencePayload,
            extractedFrameMeta,
            inferenceType,
            textPrompts,
            customPromptingData,
            isDraft = false,
            batchFlushIntervalMs = 100,
            useInferenceQueue = true,
            targetLabel,
            confidenceThreshold,
            appendMode = false,
            fallbackBelowThreshold = false,
            onRawPredictions
        }: GetPredictionsForFileParams): Promise<GetPredictionsForFileResult> => {
            // Single-label mode: override textPrompts to only include the target label
            const isSingleLabelMode = !!targetLabel;
            if (isSingleLabelMode) {
                textPrompts = [targetLabel];
            }
            const dimensions = mediaItem.metadata?.naturalDimensions;
            const file = mediaItem.file;

            if (!mediaItem.id) {
                throw new Error(`Skipping predictions for file ${file.name} - missing file_uuid`);
            }

            if (!textPrompts && promptLabels.size > 0) {
                textPrompts = Array.from(promptLabels.values()).map((label) => label.name);
            }

            if (!inferenceType) {
                inferenceType = InferenceTypes.SAM3;
            }

            const requiresPromptingData = [InferenceTypes.OWLVIT, InferenceTypes.SAM3].includes(
                inferenceType
            );

            const promptingDataToUse = customPromptingData || currentPromptingData;

            // Only makes sense to check for prompting data if we're not using the workflow inference.
            if (requiresPromptingData && promptingDataToUse.length === 0 && !textPrompts?.length) {
                console.warn(
                    `[useGetPredictions] Skipping predictions for file ${file.name} - no prompting data`,
                    {
                        requiresPromptingData,
                        customPromptingDataLength: customPromptingData?.length ?? 0,
                        currentPromptingDataLength: currentPromptingData?.length ?? 0,
                        textPromptsLength: textPrompts?.length ?? 0,
                        hasCustomPromptingData: !!customPromptingData,
                        inferenceType
                    }
                );
                return { success: false };
            }

            const requiresWorkflow = [
                InferenceTypes.WORKFLOW,
                InferenceTypes.WORKFLOW_PUBLIC
            ].includes(inferenceType);

            if (requiresWorkflow && !workflow?.id) {
                console.warn(
                    `[useGetPredictions] Skipping predictions for file ${file.name} - no workflow id`
                );
                return { success: false };
            }

            if (inferenceType === InferenceTypes.WORKFLOW && !apiKey) {
                console.warn(
                    `[useGetPredictions] Skipping predictions for file ${file.name} - no api key`
                );
                return { success: false };
            }

            // Create and manage inference queue item if requested
            let queueAbortController = abortController;
            let queueOnProgress = onProgress;

            if (useInferenceQueue && mediaItem.id) {
                // Create abort controller if not provided
                if (!queueAbortController) {
                    queueAbortController = new AbortController();
                }

                // Create queue-aware progress callback
                const originalOnProgress = onProgress;
                queueOnProgress = (progress: number) => {
                    updateInferenceQueueItemProgress(mediaItem.id!, progress);
                    originalOnProgress?.(progress);
                };

                // Create queue item
                const queueItem: InferenceQueueItem = {
                    id: mediaItem.id,
                    status: InferenceStatus.QUEUED,
                    promise: Promise.resolve(), // Will be updated below
                    abortController: queueAbortController,
                    progress: 0
                };

                // Add to queue
                addToInferenceQueue(queueItem);

                // Update status to inferring
                updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.INFERRING);
            }

            let hasSetDimensions = false;

            if (!textPrompts?.length && inferenceType === InferenceTypes.SAM3) {
                inferenceType = InferenceTypes.OWLVIT;
            }

            const { startTimeMilliseconds, endTimeMilliseconds } = inferencePayload || {};

            // zero indexed
            const startFrame = startTimeMilliseconds
                ? segmentForTargetTime(startTimeMilliseconds)
                : 0;
            const endFrame = endTimeMilliseconds
                ? segmentForTargetTime(endTimeMilliseconds)
                : mediaItem.metadata?.frame_count
                  ? mediaItem.metadata?.frame_count - 1
                  : 0;
            const expectedPredictionsCount = endFrame - startFrame + 1;

            // For hosted images (skipUpload=true), use the image URL directly
            // Otherwise, use the uploaded file reference
            const hasImageUrl = file.skipUpload && file.imageUrl;
            const upload = mediaItem.uploadResizedResult || mediaItem.uploadResult;

            if (!hasImageUrl && !upload?.filename) {
                throw new Error(`File ${file.name} not uploaded yet`);
            }

            // Prepare streaming request payload
            const resizeRatio = upload?.resizeRatio || 1;
            const streamPayload: StreamInferRequestBody = {
                fps: fps,
                startTimeMilliseconds,
                endTimeMilliseconds,
                // For hosted images, use the file ID (sourceId); backend will use imageUrl
                fileId: hasImageUrl ? mediaItem.id : upload!.filename,
                inferenceType: inferenceType,
                // Pass image URL directly to backend so it doesn't need to look up in storage
                imageUrl: hasImageUrl ? file.imageUrl : undefined
            };

            if (requiresWorkflow) {
                streamPayload.workflowId = workflow?.id!;

                if (!!apiKey) {
                    streamPayload.apiKey = apiKey;
                }

                if (file.skipUpload) {
                    streamPayload.datasetUploadMode = DatasetUploadMode.SKIP;
                } else if (!mediaItem.isVideo) {
                    streamPayload.datasetUploadMode = DatasetUploadMode.FORCE;
                }
            } else if (inferenceType === InferenceTypes.SAM3) {
                streamPayload.prompts = textPrompts?.map((text) => ({ text })) || [];
            } else {
                streamPayload.prompts = promptingDataToUse || [];
            }

            // Create isolated progress tracker for this call
            const progressTracker = createProgressTracker({ mockProgress: true });
            if (queueOnProgress) {
                progressTracker.startProgressInterval(queueOnProgress);
            }

            const processedSegments = new Set<number>();

            // Get current annotations for NMS deduplication in single-label mode
            const getCurrentAnnotationsForFrame = (fileId: string, frameId: string) => {
                return annotationsForFileAndFrameId[fileId]?.[frameId] || [];
            };

            // Create batched setter for predictions (used in full mode)
            const batchedSetPredictions = createBatchedStateSetter<
                any,
                {
                    fileId: string;
                    segment: number;
                    processedPredictions: any;
                    appendMode?: boolean;
                    labelsToUpdate?: string[];
                }
            >({
                setter: setPredictionsForFileAndFrameSegment,
                flushIntervalMs: batchFlushIntervalMs,
                preprocessQueue: (prevState, updateQueue) => {
                    const result = { ...prevState };
                    for (const update of updateQueue) {
                        const {
                            fileId,
                            segment,
                            processedPredictions,
                            appendMode: isAppendMode,
                            labelsToUpdate
                        } = update;

                        if (isAppendMode && labelsToUpdate && labelsToUpdate.length > 0) {
                            // Append mode: merge with existing predictions, only replacing for specific labels
                            // This is used for mixed inference where SAM3 and OWLv2 run in parallel
                            const existingPredictions = result[fileId]?.[segment] || [];

                            // Keep existing predictions that are NOT in the labels we're updating
                            const predictionsToKeep = existingPredictions.filter(
                                (pred: any) => !labelsToUpdate.includes(pred.class)
                            );

                            // Add all new predictions (they should already be filtered to labelsToUpdate)
                            result[fileId] = {
                                ...(result[fileId] || {}),
                                [segment]: [...predictionsToKeep, ...processedPredictions]
                            };
                        } else {
                            // Full replacement mode
                            result[fileId] = {
                                ...(result[fileId] || {}),
                                [segment]: processedPredictions
                            };
                        }
                    }
                    return result;
                }
            });

            // Create batched setter for annotations
            const batchedSetAnnotations = createBatchedStateSetter<
                any,
                {
                    fileId: string;
                    frameId: string;
                    processedAnnotations: any;
                    isSingleLabel: boolean;
                    label?: string;
                    appendMode?: boolean;
                    labelsToUpdate?: string[];
                }
            >({
                setter: setAnnotationsForFileAndFrameId,
                flushIntervalMs: batchFlushIntervalMs,
                preprocessQueue: (prevState, updateQueue) => {
                    const result = { ...prevState };

                    for (const update of updateQueue) {
                        const {
                            fileId,
                            frameId,
                            processedAnnotations,
                            isSingleLabel,
                            label,
                            appendMode: isAppendMode,
                            labelsToUpdate
                        } = update;

                        if (isSingleLabel && label) {
                            // Single-label mode: merge with existing annotations using cross-class NMS
                            // This handles cases where different classes detect the same object
                            // (e.g., "white team player" and "yellow team player" detecting same person)
                            const existingAnnotations = result[fileId]?.[frameId] || [];

                            // Cross-class NMS: compare confidences and keep higher confidence detections
                            // - If new prediction has higher confidence: keep it, remove existing
                            // - If existing has higher confidence: discard new prediction
                            const { keptPredictions, annotationIdsToRemove } =
                                crossClassNMSWithExisting(
                                    processedAnnotations,
                                    existingAnnotations,
                                    0.5 // IoU threshold
                                );

                            // Filter out annotations that were replaced by higher-confidence predictions
                            const filteredExistingAnnotations = existingAnnotations.filter(
                                (anno: any) => !annotationIdsToRemove.includes(anno.id)
                            );

                            // Combine filtered existing annotations with new kept predictions
                            result[fileId] = {
                                ...(result[fileId] || {}),
                                [frameId]: [...filteredExistingAnnotations, ...keptPredictions]
                            };
                        } else if (isAppendMode && labelsToUpdate && labelsToUpdate.length > 0) {
                            // Append mode: merge with existing annotations, only replacing for specific labels
                            // This is used for mixed inference where SAM3 and OWLv2 run in parallel
                            const existingAnnotations = result[fileId]?.[frameId] || [];

                            // Keep existing annotations that are NOT in the labels we're updating
                            const annotationsToKeep = existingAnnotations.filter(
                                (anno: any) => !labelsToUpdate.includes(anno.label)
                            );

                            // Add all new annotations (they should already be filtered to labelsToUpdate)
                            result[fileId] = {
                                ...(result[fileId] || {}),
                                [frameId]: [...annotationsToKeep, ...processedAnnotations]
                            };
                        } else {
                            // Full replacement mode
                            result[fileId] = {
                                ...(result[fileId] || {}),
                                [frameId]: processedAnnotations
                            };
                        }
                    }

                    // Update progress based on processed segments
                    // processedSegments tracks which video frames have received predictions
                    const framesWithPredictionResultsCount = processedSegments.size;

                    const progress = Math.max(
                        progressTracker.progress,
                        Math.min(
                            100,
                            Math.floor(
                                (framesWithPredictionResultsCount / expectedPredictionsCount) * 100
                            )
                        )
                    );

                    if (queueOnProgress) {
                        progressTracker.updateProgress(progress, queueOnProgress);
                    }

                    return result;
                }
            });

            const sendSharedEditHistory = async ({
                parsedPredictions,
                resizeRatio,
                segment,
                inferredDimensions
            }: {
                parsedPredictions: Prediction[];
                resizeRatio: number;
                segment: number;
                inferredDimensions: { width: number; height: number };
            }) => {
                // Only save shared edit history for SAM3 inference type (text prompting)
                if (inferenceType !== InferenceTypes.SAM3 || !textPrompts) {
                    return;
                }

                const modelUsed = "sam3";

                // Already resized to inferred dimensions
                const boxes = parsedPredictions.map((p) => ({
                    id: p.detection_id,
                    type: p.type === "mask" ? "mask" : "box",
                    label: p.class,
                    x: p.x,
                    y: p.y,
                    width: p.width,
                    height: p.height,
                    confidence: p.confidence,
                    labeledByModel: modelUsed,
                    mask: p.type === "mask" ? p.masks?.counts : undefined
                }));

                const boxIds = boxes.map((b) => b.id);
                const resultingAnnotations = {
                    key: mediaItem.isVideo ? `${mediaItem.id}/${segment}` : mediaItem.id,
                    width: inferredDimensions.width,
                    height: inferredDimensions.height,
                    boxes
                };
                const extraData = {
                    annotations: resultingAnnotations,
                    tool_data: {
                        tool_type: ToolType.RAPID_TEXT_PROMPT,
                        model_used: modelUsed,
                        text_prompts: textPrompts!.map((text) => ({
                            text,
                            confidence_threshold:
                                promptLabels.get(text)?.confidenceThreshold ||
                                DEFAULT_CONFIDENCE_THRESHOLD
                        })),
                        annotation_ids: boxIds, // ids that were created/modified by this tool
                        training_images_count: undefined,
                        prompt_boxes: undefined
                    }
                };
                return saveSharedEditHistory(mediaItem, extraData);
            };

            const onResult = async (data: any) => {
                if (abortController?.signal?.aborted) {
                    return;
                }

                const prediction = data.result;
                const inferenceItemId = data.id;

                let fileId: string;
                let segment: number;

                if (extractedFrameMeta) {
                    fileId = extractedFrameMeta.videoId;
                    segment = extractedFrameMeta.segment;
                } else {
                    // Parse fileId from inferenceItemId
                    // Formats:
                    // - Standard image: anonymous/1234-5678-9012-3456-7890
                    // - Resized image: anonymous/1234-5678-9012-3456-7890/size_max1008
                    // - Video frame: anonymous/1234-5678-9012-3456-7890/72
                    // - Hosted image (no prefix): 01MULIH3J6qqTg2s9Kou
                    const splits = inferenceItemId.split("/");

                    if (splits.length === 1) {
                        // Hosted image: no "/" in ID, use the whole ID as fileId
                        fileId = splits[0];
                    } else {
                        // Standard format: workspace/uuid or workspace/uuid/segment
                        fileId = splits[1];
                    }

                    const potentialSegment = parseInt(splits[splits.length - 1], 10);
                    // Only treat as segment if it's a number and not part of the UUID
                    if (!isNaN(potentialSegment) && splits.length > 2) {
                        segment = potentialSegment;
                    } else {
                        segment = 0;
                    }
                }
                processedSegments.add(segment);

                let inferredDimensions;

                if (
                    inferenceType === InferenceTypes.WORKFLOW ||
                    inferenceType === InferenceTypes.WORKFLOW_PUBLIC
                ) {
                    inferredDimensions = prediction.outputs?.[0]?.predictions?.image;
                } else {
                    inferredDimensions =
                        prediction.image ||
                        // seg preview doesn't return inferred image dimensions, so we use the original dimensions * resize ratio for now -- TODO: handle this better
                        (dimensions
                            ? {
                                  width: dimensions.width * resizeRatio,
                                  height: dimensions.height * resizeRatio
                              }
                            : null);
                }

                // Set inferred dimensions from the first prediction (only once)
                if (
                    !hasSetDimensions &&
                    inferredDimensions &&
                    inferredDimensions.width &&
                    inferredDimensions.height
                ) {
                    const newMetadata: MediaItemMetadata = {
                        ...mediaItem.metadata,
                        frame_count: mediaItem.metadata?.frame_count!, // this is to comply with a typescript requirement. frame_count is required. we will always have it here.
                        inferredDimensions: {
                            width: inferredDimensions.width / resizeRatio,
                            height: inferredDimensions.height / resizeRatio
                        }
                    };

                    // force currentMedia re renders after updating metadata
                    updateMediaItem({ ...mediaItem, metadata: newMetadata });
                    hasSetDimensions = true;
                }

                let parsedPredictions: Prediction[] = [];

                if (inferenceType === InferenceTypes.OWLVIT) {
                    parsedPredictions = parseOwlvitPredictions(prediction.predictions);
                } else if (inferenceType === InferenceTypes.SAM3) {
                    parsedPredictions = parseSam3Predictions(prediction);
                } else if (
                    [InferenceTypes.WORKFLOW, InferenceTypes.WORKFLOW_PUBLIC].includes(
                        inferenceType
                    )
                ) {
                    parsedPredictions = parseWorkflowPredictions(prediction);
                }

                // Call raw predictions callback if provided (for caching in Find Similar Objects)
                // Sort by confidence descending so cache is ready for progressive reveal
                if (onRawPredictions) {
                    const sortedPredictions = [...parsedPredictions].sort(
                        (a, b) => b.confidence - a.confidence
                    );
                    onRawPredictions(sortedPredictions);
                }

                // Smart filter predictions: confidence threshold + NMS + dedup against existing
                // See smartFilterPredictions() in predictionFiltering.ts for the full pipeline
                const currentAnnotations = isSingleLabelMode
                    ? getCurrentAnnotationsForFrame(fileId, mediaItem.id).filter(
                          (a: any) => a.label === targetLabel
                      )
                    : [];

                const filterResult = smartFilterPredictions(parsedPredictions, {
                    confidenceThreshold,
                    iouThreshold: 0.5,
                    fallbackBelowThreshold: fallbackBelowThreshold && isSingleLabelMode,
                    targetLabel,
                    existingAnnotations: currentAnnotations
                });

                const filteredPredictions = filterResult.predictions;

                // Process predictions with resize
                const processedPredictions: Prediction[] = filteredPredictions.map((p: any) => ({
                    ...p,
                    fileId: fileId,
                    frameId: mediaItem.id,
                    frameSegment: segment,
                    // resize predictions back to original image size if needed
                    width: p.width / resizeRatio,
                    height: p.height / resizeRatio,
                    x: p.x / resizeRatio,
                    y: p.y / resizeRatio,
                    // mark as draft
                    draft: isDraft
                }));

                // Convert to annotations
                // Use mediaItem.id as frameId to match what the canvas expects
                // For images: mediaItem.id is the file ID
                // For videos: mediaItem.id is the extracted frame ID
                const processedAnnotations: Annotation[] = processedPredictions.map((p) =>
                    predictionToAnnotation(p as Prediction, mediaItem.id)
                );

                if (isSingleLabelMode && targetLabel) {
                    // Single-label mode: Only update annotations (with NMS deduplication)
                    // Predictions store is not updated - we're adding to existing annotations
                    batchedSetAnnotations({
                        fileId,
                        frameId: mediaItem.id,
                        processedAnnotations,
                        isSingleLabel: true,
                        label: targetLabel
                    });
                } else if (appendMode && textPrompts && textPrompts.length > 0) {
                    // Append mode: merge with existing predictions/annotations for specific labels
                    // Used for mixed inference where SAM3 and OWLv2 run in parallel
                    batchedSetPredictions({
                        fileId,
                        segment,
                        processedPredictions,
                        appendMode: true,
                        labelsToUpdate: textPrompts
                    });

                    batchedSetAnnotations({
                        fileId,
                        frameId: mediaItem.id,
                        processedAnnotations,
                        isSingleLabel: false,
                        appendMode: true,
                        labelsToUpdate: textPrompts
                    });
                } else {
                    // Full mode: Save to both predictions AND annotations
                    // This is the happy path where user hasn't edited anything
                    batchedSetPredictions({ fileId, segment, processedPredictions });

                    batchedSetAnnotations({
                        fileId,
                        frameId: mediaItem.id,
                        processedAnnotations,
                        isSingleLabel: false
                    });
                }

                try {
                    // Only send shared edit history if we have valid dimensions
                    if (
                        inferredDimensions &&
                        inferredDimensions.width &&
                        inferredDimensions.height
                    ) {
                        sendSharedEditHistory({
                            parsedPredictions,
                            resizeRatio,
                            segment,
                            inferredDimensions
                        });
                    }
                } catch (error: any) {
                    // Swallow errors
                }
            };

            const eventHandlers: StreamInferEventHandlers = {
                onStarted: (data) => {},

                onResult,

                onComplete: (data) => {
                    // Flush any remaining batched updates immediately
                    batchedSetPredictions.flush();
                    batchedSetAnnotations.flush();
                    if (queueOnProgress) {
                        progressTracker.updateProgress(100, queueOnProgress);
                    }
                    progressTracker.cleanup();

                    // Ugly hack to update queue status to completed AFTER predictions are flushed to store
                    // Use setTimeout to ensure predictions propagate through React's render cycle first
                    // required so that UIs PromptAnnotateDataSidebarContent and PromptSidebarContent update in sync
                    if (useInferenceQueue && mediaItem.id) {
                        setTimeout(() => {
                            updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.COMPLETED);
                            updateInferenceQueueItemProgress(mediaItem.id, 100);
                        }, 0);
                    }
                },

                onError: (data) => {
                    console.error("Streaming inference error:", data);
                    // Cancel any pending batched updates on error
                    batchedSetPredictions.cancel();
                    batchedSetAnnotations.cancel();
                    progressTracker.cleanup();

                    // Update queue status to error
                    if (useInferenceQueue && mediaItem.id) {
                        const error =
                            data instanceof Error ? data : new Error(data?.message || String(data));
                        updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.ERROR, error);
                    }
                },

                onAborted: (data) => {
                    console.warn("Streaming inference aborted:", data);
                    // Flush any predictions we got before abort
                    batchedSetPredictions.flush();
                    progressTracker.cleanup();

                    // Update queue status to aborted with abort data for UI messaging
                    if (useInferenceQueue && mediaItem.id) {
                        const error = new Error(
                            data.reason || "Inference aborted due to too many errors"
                        );
                        updateInferenceQueueItemStatus(
                            mediaItem.id,
                            InferenceStatus.ABORTED,
                            error,
                            {
                                reason: data.reason,
                                errorCounts: data.errorCounts,
                                totalErrorCount: data.totalErrorCount,
                                threshold: data.threshold,
                                processedBeforeAbort: data.processedBeforeAbort,
                                totalItems: data.totalItems
                            }
                        );
                    }
                }
            };

            // Set up abort handler to immediately cancel the stream when abort signal is triggered
            const abortHandler = () => {
                api.inference.abortStreamInfer(
                    streamPayload.fileId,
                    streamPayload.inferenceType,
                    streamPayload.fps
                );
                batchedSetPredictions.cancel();
                batchedSetAnnotations.cancel();
                progressTracker.cleanup();
            };

            // Listen for abort signal if provided
            if (abortController?.signal) {
                if (abortController.signal.aborted) {
                    // Already aborted, don't start
                    abortHandler();
                    const abortError = new Error("Aborted");
                    abortError.name = "AbortError";
                    throw abortError;
                }
                abortController.signal.addEventListener("abort", abortHandler);
            }

            try {
                await api.inference.streamInfer(streamPayload, eventHandlers);
            } catch (error: any) {
                api.inference.abortStreamInfer(
                    streamPayload.fileId,
                    streamPayload.inferenceType,
                    streamPayload.fps
                );
                // Cancel any pending batched updates on error
                batchedSetPredictions.cancel();
                batchedSetAnnotations.cancel();
                progressTracker.cleanup();

                // Update queue status to error
                if (useInferenceQueue && mediaItem.id && error.name !== "AbortError") {
                    updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.ERROR, error);
                }

                // Always throw the error so the caller knows the operation failed
                throw error;
            } finally {
                // Clean up abort listener
                if (abortController?.signal) {
                    abortController.signal.removeEventListener("abort", abortHandler);
                }
            }

            return { success: true };
        },
        [
            currentPromptingData,
            setPredictionsForFileAndFrameSegment,
            api.inference,
            createProgressTracker,
            segmentForTargetTime,
            addToInferenceQueue,
            updateInferenceQueueItemStatus,
            updateInferenceQueueItemProgress,
            workflow,
            apiKey,
            promptLabels,
            updateMediaItem,
            setAnnotationsForFileAndFrameId,
            flow,
            fps,
            step,
            saveSharedEditHistory,
            annotationsForFileAndFrameId,
            predictionsForFileAndFrameSegment
        ]
    );

    const generateModel = useCallback(async () => {
        // Main function to generate predictions for all image files using combined training data
        if (currentPromptingData.length === 0) {
            throw new Error("No annotations found. Please annotate some files first.");
        }

        if (completedMediaItems.length === 0) {
            throw new Error("Please wait for files to upload before generating a model.");
        }

        // Set loading state
        setFlowLocked(true);

        try {
            // Clear existing predictions and queue for all files
            completedMediaItems.forEach((item) => {
                clearPredictionsForFile(item.id);
            });
            clearQueue();

            // Separate labels by diff status for mixed inference
            const labelsWithDiffs: string[] = [];
            const labelsWithoutDiffs: string[] = [];

            promptLabels.forEach((label, name) => {
                if (label.hasDiffs) {
                    labelsWithDiffs.push(name);
                } else {
                    labelsWithoutDiffs.push(name);
                }
            });

            // Determine if we're running mixed inference
            const hasLabelsWithDiffs =
                labelsWithDiffs.length > 0 && currentPromptingData.length > 0;
            const hasLabelsWithoutDiffs = labelsWithoutDiffs.length > 0;
            const isMixedInference = hasLabelsWithDiffs && hasLabelsWithoutDiffs;

            // Filter prompting data to only include boxes for OWLv2 labels
            // This prevents sending SAM3-only labels as box prompts to OWLv2
            const owlv2PromptingData = currentPromptingData
                .map((promptData) => ({
                    ...promptData,
                    boxes: promptData.boxes.filter((box: any) => labelsWithDiffs.includes(box.cls))
                }))
                .filter((promptData) => promptData.boxes.length > 0);

            // Calculate dynamic confidence threshold for OWLv2 based on prompt count
            const totalPromptCount = owlv2PromptingData.reduce(
                (sum, data) => sum + data.boxes.length,
                0
            );
            const boxConfidenceThreshold = calculateBoxPromptConfidenceThreshold(totalPromptCount);

            // Generate predictions for all files
            // We manage the inference queue externally to coordinate mixed inference completion
            const mediaItemPromises: Promise<void>[] = [];

            // Start inference for all files
            completedMediaItems.forEach((mediaItem) => {
                if (!mediaItem.id) return;

                // Create abort controller for this media item
                const abortController = new AbortController();

                // Add to inference queue with INFERRING status
                addToInferenceQueue({
                    id: mediaItem.id,
                    status: InferenceStatus.INFERRING,
                    promise: Promise.resolve(),
                    abortController,
                    progress: 0
                });

                // Collect promises for this media item
                const itemPromises: Promise<void>[] = [];

                // Progress callback to update the externally-managed queue item
                const onProgress = (progress: number) => {
                    updateInferenceQueueItemProgress(mediaItem.id!, progress);
                };

                // SAM3 text prompting for labels WITHOUT diffs
                if (hasLabelsWithoutDiffs) {
                    itemPromises.push(
                        getPredictionsForFile({
                            mediaItem,
                            useInferenceQueue: false, // Managed externally
                            reviewModel: true,
                            inferenceType: InferenceTypes.SAM3,
                            textPrompts: labelsWithoutDiffs,
                            appendMode: isMixedInference,
                            abortController,
                            onProgress
                        }).catch((error: any) => {
                            if (error.name === "AbortError") return;
                            console.error(
                                `ModelGeneration: Error generating SAM3 predictions for ${mediaItem.file.name}:`,
                                error
                            );
                        })
                    );
                }

                // OWLv2 box prompting for labels WITH diffs
                if (hasLabelsWithDiffs && owlv2PromptingData.length > 0) {
                    itemPromises.push(
                        getPredictionsForFile({
                            mediaItem,
                            useInferenceQueue: false, // Managed externally
                            reviewModel: true,
                            inferenceType: InferenceTypes.OWLVIT,
                            textPrompts: labelsWithDiffs,
                            customPromptingData: owlv2PromptingData,
                            confidenceThreshold: boxConfidenceThreshold,
                            appendMode: isMixedInference,
                            abortController,
                            onProgress
                        }).catch((error: any) => {
                            if (error.name === "AbortError") return;
                            console.error(
                                `ModelGeneration: Error generating OWLv2 predictions for ${mediaItem.file.name}:`,
                                error
                            );
                        })
                    );
                }

                // Wait for all inference for this media item, then mark as completed
                const mediaItemPromise = Promise.all(itemPromises).then(() => {
                    updateInferenceQueueItemStatus(mediaItem.id, InferenceStatus.COMPLETED);
                    updateInferenceQueueItemProgress(mediaItem.id, 100);
                });

                mediaItemPromises.push(mediaItemPromise);
            });

            // Wait for all media items to complete (but don't block the UI)
            Promise.all(mediaItemPromises).catch((error) => {
                console.error("[generateModel] Error waiting for all inferences:", error);
            });

            return { success: true, queuedCount: completedMediaItems.length };
        } catch (error: any) {
            console.error("[useGetPredictions] error", error);
            throw error;
        } finally {
            setFlowLocked(false);
        }
    }, [
        setFlowLocked,
        currentPromptingData,
        completedMediaItems,
        clearPredictionsForFile,
        getPredictionsForFile,
        clearQueue,
        promptLabels,
        addToInferenceQueue,
        updateInferenceQueueItemStatus,
        updateInferenceQueueItemProgress
    ]);

    return {
        generateModel,
        getPredictionsForFile,

        // Inference queue-related functions
        inferenceQueue: getInferenceQueue,
        getInferenceQueueItemById,
        updateInferenceQueueItemProgress,
        clearInferenceQueue: clearQueue,
        isLoadingInferenceQueue,
        errorInferenceQueue
    };
};
